// @givmo/employer-module — a charity-collection gift's mark and its charities (EP-WEB40; Company
// Profiles' CAM-R136, D-CAM-111 and D-CAM-113).
//
// A collection gift is named by its collection where the server names it, and by Jonathan's words where
// it does not ("Private Charity Collection", "Charity Collection"). Its mark is the collection's cover
// beside its name, else a neutral lock: a Private collection's cover is its owner's own picture. And
// it opens its charities, each with its share of the match, from a control that says so:
// "{N} charities ›" (D-CAM-113, Jonathan: "it has to be clear they can click into Charity Collection
// and see all of the underlying charities"). The charities never name the employee.
//
// A share is shown only where it adds up to the amount stated beside it: an approved request's booked
// slices; a waiting request's shares of the match approving it would book now (`collection-shares.ts`),
// with the ruled sentence where that match is no longer the one the list showed (D-EP-56). Otherwise
// the charities are named without amounts, as any waiting grouped request's are.

import { Avatar, Icon, Skeleton, testIdProp } from '@givmo/design-system';
import { ChevronRight } from 'lucide-react';
import { LockMark } from '../../components/lock-mark';
import { useCollectionShares } from './collection-shares';
import {
  charityCountLabel,
  collectionLocked,
  collectionTitle,
  formatCentsUsd,
  type ApprovalStatus,
  type CollectionLabel,
  type GroupedMember,
} from './dashboard-decode';

/** The collection's mark: its cover (initials when it has none) beside its name, else the lock. */
export function CollectionMark({
  label,
  size,
  testId,
}: {
  label: CollectionLabel;
  size: 'sm' | 'lg';
  testId?: string;
}) {
  if (collectionLocked(label)) return <LockMark size={size} testId={testId} />;
  return (
    <Avatar
      src={label.coverUrl}
      name={collectionTitle(label)}
      size={size}
      className={size === 'lg' ? 'border border-border-subtle' : undefined}
      testId={testId}
    />
  );
}

/** "{N} charities ›": the control that opens a collection gift's charities, saying it does. */
export function CharitiesToggle({
  count,
  title,
  expanded,
  onToggle,
  controls,
  testId,
}: {
  count: number;
  /** The collection's title, for the control's name ("Show the 3 charities in …"). */
  title: string;
  expanded: boolean;
  onToggle: () => void;
  /** The id of what it opens. */
  controls: string;
  testId: string;
}) {
  const charities = charityCountLabel(count);
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={`${expanded ? 'Hide' : 'Show'} the ${charities} in ${title}`}
      onClick={(e) => {
        // A row that is itself a control must not fire too.
        e.stopPropagation();
        onToggle();
      }}
      className="inline-flex items-center gap-2 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
      {...testIdProp(testId)}
    >
      {charities}
      <Icon
        icon={ChevronRight}
        size={14}
        aria-hidden
        className={expanded ? 'rotate-90 transition-transform' : 'transition-transform'}
      />
    </button>
  );
}

/** What the request a collection gift belongs to says about its amount. */
export interface CollectionRequest {
  /** Every leg's match id, the request's identity on `/pending`. */
  matchIds: readonly string[];
  status: ApprovalStatus;
  /** Its charities, in the request's order. */
  members: readonly GroupedMember[];
  /** The amount the surface states for it now, in cents: what the shares must add up to. */
  statedCents: number | null;
}

/** The ruled sentence for a match that moved since the list loaded (D-EP-56, 2026-10-05 20:36). */
function changedSentence(cents: number): string {
  return `Your match budget changed since this list loaded, so this match is now ${formatCentsUsd(cents)}.`;
}

/** The waiting request's shares as they may be shown: in cents by leg, or a reason there are none. */
function waitingShares(
  request: CollectionRequest,
  read: ReturnType<typeof useCollectionShares>,
  explainChange: boolean,
):
  | { kind: 'loading' }
  | { kind: 'shown'; cents: ReadonlyMap<string, number>; changedTo: number | null }
  | { kind: 'unavailable' }
  | { kind: 'none' } {
  if (read.status === 'loading') return { kind: 'loading' };
  if (read.status === 'unavailable') return { kind: 'unavailable' };
  const { figure, cents } = read.shares;
  if (figure.kind !== 'available' || cents === null) return { kind: 'none' };
  let sum = 0;
  for (const member of request.members) {
    const share = cents.get(member.matchId);
    if (share === undefined) return { kind: 'none' };
    sum += share;
  }
  // Never a split that does not add up to the match it splits.
  if (sum !== figure.cents) return { kind: 'none' };
  if (request.statedCents === figure.cents) return { kind: 'shown', cents, changedTo: null };
  return explainChange ? { kind: 'shown', cents, changedTo: figure.cents } : { kind: 'none' };
}

const QUIET_LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

/**
 * A collection gift's charities, each with its share where one may be shown. `explainChange` states
 * the ruled sentence when the match moved since the list loaded (the Approvals list); the approve
 * dialog states its own and passes false, so a share that does not add up to its amount never shows.
 * `since` is when the stated amount was read (the list's load, the dialog's open): the shares are
 * read no earlier, so the sentence only ever names a figure newer than the one beside it.
 */
export function CollectionCharities({
  id,
  request,
  explainChange,
  since,
  testId,
}: {
  id: string;
  request: CollectionRequest;
  explainChange: boolean;
  since: number;
  testId: string;
}) {
  const waiting = request.status === 'Pending';
  const read = useCollectionShares(request.matchIds, waiting, since);
  const shares = waiting ? waitingShares(request, read, explainChange) : null;

  const amountOf = (member: GroupedMember) => {
    if (!waiting) return member.amount;
    if (shares?.kind !== 'shown') return null;
    return formatCentsUsd(shares.cents.get(member.matchId) ?? null);
  };

  return (
    // Bounded, so a share stays beside its charity across a wide table row, and within the screen
    // where the table scrolls sideways on a phone.
    <div
      id={id}
      className="flex max-w-[min(30rem,calc(100vw_-_6rem))] flex-col gap-8"
      {...testIdProp(testId)}
    >
      {shares?.kind === 'shown' && shares.changedTo !== null && (
        <p className={QUIET_LINE} {...testIdProp(`${testId}-changed`)}>
          {changedSentence(shares.changedTo)}
        </p>
      )}
      <ul className="flex flex-col gap-6" aria-label="Charities">
        {request.members.map((member, index) => {
          const amount = amountOf(member);
          return (
            <li
              // Two legs can name the same charity, so the index is part of the key.
              key={`${member.matchId}-${index}`}
              className="flex items-center justify-between gap-16"
              {...testIdProp(`${testId}-item-${index}`)}
            >
              <span className="flex min-w-0 items-center gap-8">
                <Avatar src={member.logoUrl} name={member.charityName} size="xs" />
                <span className="min-w-0 truncate font-sans text-s13 tracking-givmo text-text">
                  {member.charityName}
                </span>
              </span>
              {shares?.kind === 'loading' ? (
                <Skeleton className="h-12 w-[40px] shrink-0" />
              ) : amount !== null ? (
                <span className="shrink-0 font-sans text-s13 tracking-givmo text-text-secondary tabular-nums">
                  {amount}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {shares?.kind === 'unavailable' && (
        <p className={QUIET_LINE} {...testIdProp(`${testId}-unavailable`)}>
          We couldn’t load each charity’s share right now.
        </p>
      )}
    </div>
  );
}
